import React from 'react';
import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { FormattedMessage } from 'react-intl';
import { IErrorParsing } from '../../../../../infrastructure/error-parsing';
import { IAlertsService } from '../../../../../infrastructure/alerts';
import { alertServerError } from '../../../../global/modal/alerts-host/alert-templates';
import TrackingServiceProvider from '../../../../../tracking-service/tracking-service-provider';
import { TRACKING_EVENTS } from '../../../../../tracking-service/tracking-values';
import { IGlobalStorage } from '../../../../../infrastructure/global-storage';
import { IAssetsDAO } from '../../../../../api/assets.dao';
import { LONG_RICH_TEXT_LENGTH } from '../../../../../constants/common.constants';
import { calcByteCodeSize } from '../../../../../infrastructure/utils/common.utils';
import { hasPermission } from '../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../api/roles.dao';
import { IAttendeesDAO } from '../../../../../api/attendees.dao';
import { transpileClassesToInlineStyles } from '../../../../global/rich-text-editor/utils/transpiler';

export class AssetsBiographyFormViewModel {
    @mobx.observable
    public biography: string;
    @mobx.observable
    public isLoading: boolean;
    public permissionToUpdate: boolean;
    constructor(
        public readonly accountId: number,
        public readonly eventId: number,
        public readonly organizationId: number | null,
        public readonly attendeeId: number | null,
        biography: string,
        public closeAssetsBiographyPopup: () => void,
        public setSpeakerBiography: (biography: string, organizationId: number, attendeeId: number) => void,
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly assetsDAO: IAssetsDAO = IoC.Container.get(IAssetsDAO),
        @IoC.Inject private readonly attendeesDAO: IAttendeesDAO = IoC.Container.get(IAttendeesDAO),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject private readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage)
    ) {
        this.biography = biography || '';
        this.isLoading = false;
        this.permissionToUpdate = hasPermission(Permission.UPDATE_ATTENDEE);
    }

    public static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    }

    public getBiographyError = (): JSX.Element | null => {
        return calcByteCodeSize(this.biography) > LONG_RICH_TEXT_LENGTH
            ? <FormattedMessage
                id='oe.attendee.biographyTooLong'
                description='Warning message'
                defaultMessage='Biography is too long'
            />
            : null;
    };

    @mobx.action
    public setBiography = (value: string): void => {
        this.biography = value;
    };

    @mobx.action
    public updateBiography = async (): Promise<void> => {
        try {
            mobx.runInAction(() => {
                this.isLoading = true;
            });
            const params = {
                eventId: this.eventId,
                accountId: this.accountId,
                attendeeId: this.attendeeId,
                // Since CKEditor works with classes, but data is stored with inline styles,
                // all CKEditor values need to be converted to the appropriate format
                biography: transpileClassesToInlineStyles(this.biography)
            };
            await this.attendeesDAO.updateBiography(params);
            this.trackAssetBiographyMixPanel(TRACKING_EVENTS.UPDATE_BIOGRAPHY);
            this.closeAssetsBiographyPopup();
            this.setSpeakerBiography(this.biography, this.organizationId, this.attendeeId);
        } catch (e) {
            AssetsBiographyFormViewModel.handleApiException(e);
        } finally {
            mobx.runInAction(() => {
                this.isLoading = false;
            });
        }
    };

    public getMixinAccountNameAndEventId = (): { [key: string]: string } => ({
        'Account Name': this.globalStorage.getEvent()?.accountName,
        'Account ID': this.globalStorage.getEvent()?.accountId.toString(),
        'Event ID': this.eventId.toString(),
        'Event Name': this.globalStorage.getEvent()?.name,
        'Attendee ID': this.attendeeId.toString()
    });

    public trackAssetBiographyMixPanel = (trackingEvent: TRACKING_EVENTS): void => {
        this.trackingService.get().track(trackingEvent, {
            ...this.getMixinAccountNameAndEventId()
        });
    };
}
